import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const MENU_WIDTH = 236;
const MENU_ESTIMATED_HEIGHT = 340;
const VIEWPORT_GAP = 8;

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function StockBalanceRowActions({
  rowId,
  canPost,
  postingActions,
  onPost,
  onShowBatches,
  onShowMovements,
  menuLabel,
  showBatchesLabel,
  showMovementsLabel,
}) {
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  function closeMenu({ returnFocus = false } = {}) {
    setOpen(false);
    if (returnFocus) {
      window.requestAnimationFrame(() => buttonRef.current?.focus());
    }
  }

  function openMenu() {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!rect) return;

    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const left = clamp(rect.right - MENU_WIDTH, VIEWPORT_GAP, Math.max(VIEWPORT_GAP, viewportWidth - MENU_WIDTH - VIEWPORT_GAP));
    const belowTop = rect.bottom + 6;
    const top = belowTop + MENU_ESTIMATED_HEIGHT <= viewportHeight
      ? belowTop
      : Math.max(VIEWPORT_GAP, rect.top - MENU_ESTIMATED_HEIGHT - 6);

    setPosition({ top, left });
    setOpen(true);
  }

  useEffect(() => {
    if (!open) return undefined;

    const focusTimer = window.setTimeout(() => {
      menuRef.current?.querySelector('[role="menuitem"]:not(:disabled)')?.focus();
    }, 0);

    const handlePointerDown = (event) => {
      if (menuRef.current?.contains(event.target) || buttonRef.current?.contains(event.target)) return;
      closeMenu();
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeMenu({ returnFocus: true });
        return;
      }

      if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
      const items = Array.from(menuRef.current?.querySelectorAll('[role="menuitem"]:not(:disabled)') || []);
      if (!items.length) return;

      event.preventDefault();
      const currentIndex = items.indexOf(document.activeElement);
      let nextIndex = 0;
      if (event.key === "End") nextIndex = items.length - 1;
      else if (event.key === "Home") nextIndex = 0;
      else if (event.key === "ArrowDown") nextIndex = currentIndex < 0 ? 0 : (currentIndex + 1) % items.length;
      else nextIndex = currentIndex < 0 ? items.length - 1 : (currentIndex - 1 + items.length) % items.length;
      items[nextIndex]?.focus();
    };

    const handleViewportChange = () => closeMenu();

    document.addEventListener("pointerdown", handlePointerDown, true);
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", handleViewportChange);
    window.addEventListener("scroll", handleViewportChange, true);

    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener("pointerdown", handlePointerDown, true);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", handleViewportChange);
      window.removeEventListener("scroll", handleViewportChange, true);
    };
  }, [open]);

  const menuId = `stock-balance-actions-${rowId}`;

  return (
    <div className="stock-row-actions">
      <button
        ref={buttonRef}
        type="button"
        className="stock-row-actions__trigger"
        aria-label={menuLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        title={menuLabel}
        onClick={() => (open ? closeMenu() : openMenu())}
      >
        <span aria-hidden="true">⋯</span>
      </button>

      {open && createPortal(
        <div
          ref={menuRef}
          id={menuId}
          className="stock-row-actions__menu"
          role="menu"
          aria-label={menuLabel}
          style={{ top: position.top, left: position.left }}
        >
          {canPost && postingActions.map((item) => (
            <button
              key={item.value}
              type="button"
              role="menuitem"
              onClick={() => {
                closeMenu();
                onPost?.(item.value);
              }}
            >
              <span className="stock-row-actions__menu-icon" aria-hidden="true">{item.icon || "→"}</span>
              <span>{item.label}</span>
            </button>
          ))}

          {canPost && <div className="stock-row-actions__separator" role="separator" />}

          <button
            type="button"
            role="menuitem"
            onClick={() => {
              closeMenu();
              onShowBatches?.();
            }}
          >
            <span className="stock-row-actions__menu-icon" aria-hidden="true">LOT</span>
            <span>{showBatchesLabel}</span>
          </button>

          <button
            type="button"
            role="menuitem"
            onClick={() => {
              closeMenu();
              onShowMovements?.();
            }}
          >
            <span className="stock-row-actions__menu-icon" aria-hidden="true">↕</span>
            <span>{showMovementsLabel}</span>
          </button>
        </div>,
        document.body
      )}
    </div>
  );
}

export default StockBalanceRowActions;
